import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { challengesApi } from '../lib/api';
import { Trophy, Users, Target, Flame, Plus, ArrowRight, Calendar, Zap, ChevronRight, Clock } from 'lucide-react';

const TYPE_CONFIG = {
  workout_count: { icon: Target, label: 'Workout Count', color: '#E63946' },
  streak: { icon: Flame, label: 'Streak', color: '#FF6B35' },
  custom_metric: { icon: Zap, label: 'Custom Goal', color: '#00F0FF' },
};

function daysLeft(endDate) {
  const diff = new Date(endDate) - Date.now();
  const days = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  return days;
}

function ChallengeCard({ challenge, onJoin, token }) {
  const cfg = TYPE_CONFIG[challenge.type] || TYPE_CONFIG.workout_count;
  const Icon = cfg.icon;
  const remaining = daysLeft(challenge.end_date);
  const pct = challenge.my_progress ? Math.min(100, Math.round((challenge.my_progress / challenge.target_value) * 100)) : 0;

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 hover:border-zinc-700 transition-all group" data-testid={`challenge-card-${challenge.challenge_id}`}>
      {/* Header stripe */}
      <div className="h-1" style={{ background: cfg.color }} />

      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 flex items-center justify-center border border-zinc-700 flex-shrink-0" style={{ color: cfg.color, borderColor: `${cfg.color}30` }}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <Link to={`/community/challenge/${challenge.challenge_id}`} className="text-white text-base font-medium hover:text-[#00F0FF] transition-colors block" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '1.1rem' }} data-testid={`challenge-title-${challenge.challenge_id}`}>
              {challenge.title}
            </Link>
            <span className="text-zinc-500 text-[10px] uppercase tracking-wider">{cfg.label}</span>
          </div>
        </div>

        {challenge.description && (
          <p className="text-zinc-400 text-sm mb-3 line-clamp-2 leading-relaxed">{challenge.description}</p>
        )}

        {/* Target + Stats */}
        <div className="flex items-center gap-4 mb-3">
          <span className="text-zinc-500 text-xs flex items-center gap-1"><Target className="w-3 h-3" /> {challenge.target_value} {challenge.unit}</span>
          <span className="text-zinc-500 text-xs flex items-center gap-1"><Users className="w-3 h-3" /> {challenge.participants_count} joined</span>
          <span className={`text-xs flex items-center gap-1 ${remaining <= 3 ? 'text-[#E63946]' : 'text-zinc-500'}`}><Clock className="w-3 h-3" /> {remaining}d left</span>
        </div>

        {/* Progress bar if joined */}
        {challenge.joined && (
          <div className="mb-3">
            <div className="flex justify-between text-[10px] uppercase tracking-wider mb-1">
              <span className="text-zinc-500">Your Progress</span>
              <span style={{ color: cfg.color }}>{pct}%</span>
            </div>
            <div className="h-1.5 bg-zinc-800 overflow-hidden">
              <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: cfg.color }} />
            </div>
          </div>
        )}

        {/* Action */}
        <div className="flex justify-between items-center pt-3 border-t border-zinc-800/60">
          {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
          {challenge.joined ? (
            <Link to={`/community/challenge/${challenge.challenge_id}`}
              className="text-xs uppercase tracking-wider hover:text-white transition-colors flex items-center gap-1"
              style={{ fontFamily: 'Bebas Neue, sans-serif', color: cfg.color }}
              data-testid={`view-challenge-${challenge.challenge_id}`}
            >
              View Progress <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          ) : token ? (
            <button onClick={() => onJoin(challenge.challenge_id)}
              className="px-4 py-1.5 text-xs uppercase tracking-wider text-white transition-colors hover:opacity-80"
              style={{ fontFamily: 'Bebas Neue, sans-serif', background: cfg.color }}
              data-testid={`join-challenge-${challenge.challenge_id}`}
            >
              Join Challenge
            </button>
          ) : (
            <Link to="/auth" className="text-xs text-zinc-500 hover:text-white transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Sign in to join
            </Link>
          )}
          <Link to={`/community/challenge/${challenge.challenge_id}`}
            className="text-xs text-zinc-600 hover:text-zinc-300 transition-colors flex items-center gap-1"
            data-testid={`leaderboard-link-${challenge.challenge_id}`}
          >
            <Trophy className="w-3 h-3" /> Leaderboard
          </Link>
        </div>
      </div>
    </div>
  );
}

function CreateChallengeForm({ token, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', type: 'workout_count',
    target_value: '', unit: 'workouts', end_date: '',
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.title.trim()) { setError('Title required'); return; }
    if (!form.target_value || Number(form.target_value) <= 0) { setError('Target must be a positive number'); return; }
    if (!form.end_date) { setError('End date required'); return; }

    setCreating(true);
    try {
      await challengesApi.create({
        ...form,
        target_value: Number(form.target_value),
      }, token);
      setForm({ title: '', description: '', type: 'workout_count', target_value: '', unit: 'workouts', end_date: '' });
      setOpen(false);
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create challenge');
    }
    setCreating(false);
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="w-full bg-zinc-900/40 border border-dashed border-zinc-700 p-4 text-zinc-500 text-sm hover:border-zinc-500 hover:text-zinc-300 transition-colors flex items-center justify-center gap-2"
        data-testid="create-challenge-trigger"
      >
        <Plus className="w-4 h-4" /> Create a Challenge
      </button>
    );
  }

  const types = [
    { value: 'workout_count', label: 'Workout Count', hint: 'Complete X workouts' },
    { value: 'streak', label: 'Streak', hint: 'Maintain a daily streak' },
    { value: 'custom_metric', label: 'Custom Goal', hint: 'Track any metric' },
  ];

  return (
    <form onSubmit={handleSubmit} className="bg-zinc-900/60 border border-zinc-800 p-5" data-testid="create-challenge-form">
      <h3 className="text-white text-sm uppercase tracking-wider mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>New Challenge</h3>

      <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        placeholder="Challenge title (e.g., 30-Day HYROX Prep)" maxLength={100}
        className="w-full bg-zinc-800/50 border border-zinc-700 text-white text-sm px-3 py-2 mb-3 placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
        data-testid="challenge-title-input" />

      <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
        placeholder="Description (optional)" rows={2}
        className="w-full bg-zinc-800/50 border border-zinc-700 text-white text-sm px-3 py-2 mb-3 placeholder-zinc-600 focus:outline-none focus:border-zinc-500 resize-none"
        data-testid="challenge-desc-input" />

      <div className="flex gap-2 mb-3 flex-wrap">
        {types.map(t => (
          <button key={t.value} type="button" onClick={() => setForm(f => ({ ...f, type: t.value }))}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider transition-colors ${form.type === t.value ? 'bg-[#E63946] text-white' : 'bg-zinc-800 text-zinc-400 border border-zinc-700'}`}
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >{t.label}</button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <input type="number" value={form.target_value} onChange={e => setForm(f => ({ ...f, target_value: e.target.value }))}
          placeholder="Target" min="1"
          className="bg-zinc-800/50 border border-zinc-700 text-white text-sm px-3 py-2 placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
          data-testid="challenge-target-input" />
        <input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
          placeholder="Unit (e.g., km, workouts)"
          className="bg-zinc-800/50 border border-zinc-700 text-white text-sm px-3 py-2 placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
          data-testid="challenge-unit-input" />
        <input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
          className="bg-zinc-800/50 border border-zinc-700 text-white text-sm px-3 py-2 focus:outline-none focus:border-zinc-500"
          data-testid="challenge-end-date-input" />
      </div>

      {error && <p className="text-[#E63946] text-xs mb-3">{error}</p>}

      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-xs text-zinc-400 hover:text-white transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Cancel</button>
        <button type="submit" disabled={creating}
          className="px-5 py-2 bg-[#E63946] text-white text-xs uppercase tracking-wider disabled:opacity-40 hover:bg-[#E63946]/80 transition-colors"
          style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="create-challenge-submit"
        >{creating ? 'Creating...' : 'Launch Challenge'}</button>
      </div>
    </form>
  );
}

export default function ChallengesPage() {
  const { user, token } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [filter, setFilter] = useState('active');
  const [loading, setLoading] = useState(true);

  const loadChallenges = useCallback(async () => {
    setLoading(true);
    try {
      const res = await challengesApi.list(filter, token);
      setChallenges(res.data.challenges || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filter, token]);

  useEffect(() => { loadChallenges(); }, [loadChallenges]);

  async function handleJoin(challengeId) {
    if (!token) return;
    try {
      await challengesApi.join(challengeId, token);
      loadChallenges();
    } catch (e) { console.error(e); }
  }

  return (
    <main className="pt-20 min-h-screen" data-testid="challenges-page">
      <div className="container-ninja section-spacing">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <Link to="/community" className="text-zinc-500 text-xs uppercase tracking-wider hover:text-zinc-300 transition-colors mb-3 inline-block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Community
            </Link>
            <h1 className="text-white text-4xl sm:text-5xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="challenges-title">
              Challenges
            </h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-md">
              Compete with the community. Set goals, track progress, climb the leaderboard.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8 text-[#E63946]/60" />
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 mb-6 border-b border-zinc-800" data-testid="challenges-filter">
          {['active', 'completed', 'all'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-5 py-3 text-xs uppercase tracking-wider transition-colors ${filter === f ? 'text-[#E63946] border-b-2 border-[#E63946]' : 'text-zinc-500 hover:text-zinc-300'}`}
              style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid={`filter-${f}`}
            >{f}</button>
          ))}
        </div>

        {/* Create challenge */}
        {user && token && filter === 'active' && (
          <div className="mb-6">
            <CreateChallengeForm token={token} onCreated={loadChallenges} />
          </div>
        )}

        {/* Challenge grid */}
        {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-48 bg-zinc-900/50 animate-pulse border border-zinc-800" />)}
          </div>
        ) : challenges.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {challenges.map(c => (
              <ChallengeCard key={c.challenge_id} challenge={c} onJoin={handleJoin} token={token} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-zinc-900/40 border border-zinc-800">
            <Trophy className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400 text-sm mb-2">
              {filter === 'active' ? 'No active challenges yet.' : 'No challenges found.'}
            </p>
            {user && filter === 'active' && (
              <p className="text-zinc-500 text-xs">Be the first to create a challenge!</p>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
